// Insurance module: policy types, premium frequencies and the small calculations
// shared by the policy list and the policy details page. All data is entered by hand.
import React from 'react';
import {
  FamilyRestroomOutlined,
  UmbrellaOutlined,
  HealthAndSafetyOutlined,
  DirectionsCarOutlined,
  HomeOutlined,
  FlightOutlined,
  VerifiedUserOutlined,
  ShieldOutlined,
} from '@mui/icons-material';

export const POLICY_TYPES = [
  { value: 'term', label: 'Term Life', Icon: FamilyRestroomOutlined, color: '#2563eb', insuredHint: 'e.g., Self' },
  { value: 'life', label: 'Life / Endowment', Icon: UmbrellaOutlined, color: '#7c3aed', insuredHint: 'e.g., Self' },
  { value: 'health', label: 'Health', Icon: HealthAndSafetyOutlined, color: '#16a34a', insuredHint: 'e.g., Self, Spouse, 2 Kids' },
  { value: 'motor', label: 'Motor', Icon: DirectionsCarOutlined, color: '#ea580c', insuredHint: 'e.g., MH 01 AB 1234 (Honda City)' },
  { value: 'home', label: 'Home', Icon: HomeOutlined, color: '#0891b2', insuredHint: 'e.g., Flat 402, Pune' },
  { value: 'travel', label: 'Travel', Icon: FlightOutlined, color: '#db2777', insuredHint: 'e.g., Family trip to Europe' },
  { value: 'accident', label: 'Personal Accident', Icon: VerifiedUserOutlined, color: '#ca8a04', insuredHint: 'e.g., Self' },
  { value: 'other', label: 'Other', Icon: ShieldOutlined, color: '#64748b', insuredHint: 'Person, item or property covered' },
];

export const policyType = (value) => POLICY_TYPES.find(t => t.value === value) || POLICY_TYPES[POLICY_TYPES.length - 1];

export const PolicyTypeIcon = ({ type, ...props }) => {
  const { Icon } = policyType(type);
  return <Icon {...props} />;
};

// months = gap between premiums; perYear = premiums in a year (single = one-time)
export const PREMIUM_FREQUENCIES = [
  { value: 'monthly', label: 'Monthly', short: '/month', months: 1, perYear: 12 },
  { value: 'quarterly', label: 'Quarterly', short: '/quarter', months: 3, perYear: 4 },
  { value: 'half_yearly', label: 'Half-yearly', short: '/half-year', months: 6, perYear: 2 },
  { value: 'yearly', label: 'Yearly', short: '/year', months: 12, perYear: 1 },
  { value: 'single', label: 'Single (one-time)', short: ' one-time', months: 0, perYear: 0 },
];

export const frequency = (value) => PREMIUM_FREQUENCIES.find(f => f.value === value) || PREMIUM_FREQUENCIES[3];

export const POLICY_STATUSES = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'lapsed', label: 'Lapsed', color: 'error' },
  { value: 'matured', label: 'Matured', color: 'info' },
  { value: 'surrendered', label: 'Surrendered', color: 'default' },
  { value: 'expired', label: 'Expired', color: 'default' },
];

export const policyStatus = (value) => POLICY_STATUSES.find(s => s.value === value) || POLICY_STATUSES[0];

// Common Indian insurers, offered as suggestions (any name can be typed)
export const INSURERS = [
  'LIC', 'HDFC Life', 'ICICI Prudential Life', 'SBI Life', 'Max Life', 'Tata AIA Life', 'Bajaj Allianz Life',
  'Kotak Life', 'Aditya Birla Sun Life', 'PNB MetLife', 'Star Health', 'Niva Bupa', 'Care Health',
  'HDFC ERGO', 'ICICI Lombard', 'Bajaj Allianz General', 'Tata AIG', 'New India Assurance',
  'United India Insurance', 'National Insurance', 'Oriental Insurance', 'Digit Insurance', 'Acko',
  'SBI General', 'Reliance General', 'Manipal Cigna', 'Aditya Birla Health',
];

// Premium for a whole year (single premium counts as 0 per year)
export const annualPremium = (policy) => (Number(policy?.premium_amount) || 0) * frequency(policy?.premium_frequency).perYear;

// ---- Dates (stored as local YYYY-MM-DD, never shifted to UTC) ----
export const parseDate = (value) => {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return y ? new Date(y, m - 1, d) : null;
};

export const toDateString = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const formatDate = (value) => {
  const date = value instanceof Date ? value : parseDate(value);
  return date ? date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
};

// Add whole months, keeping the day where possible (31 Jan + 1 month → 28/29 Feb)
export const addMonths = (date, months) => {
  const result = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(date.getDate(), lastDay));
  return result;
};

// The due date after this one, based on how often the premium is paid
export const nextDueAfter = (dueDate, premiumFrequency) => {
  const { months } = frequency(premiumFrequency);
  if (!dueDate || !months) return null;
  return addMonths(dueDate, months);
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

export const daysUntil = (value) => {
  const date = parseDate(value);
  if (!date) return null;
  return Math.round((date - startOfToday()) / 86400000);
};

export const DUE_SOON_DAYS = 30;

// Premium / renewal status of an active policy, for chips and alerts
export const dueInfo = (policy) => {
  if (!policy || policy.status !== 'active') return null;
  const days = daysUntil(policy.next_due_date);
  if (days === null) return null;
  if (days < 0) return { days, severity: 'error', label: `Overdue by ${-days} day${days === -1 ? '' : 's'}` };
  if (days === 0) return { days, severity: 'error', label: 'Due today' };
  if (days <= DUE_SOON_DAYS) return { days, severity: 'warning', label: `Due in ${days} day${days === 1 ? '' : 's'}` };
  return { days, severity: 'default', label: `Due ${formatDate(policy.next_due_date)}` };
};

// An active policy whose end date has passed: suggest marking it matured / expired
export const hasEnded = (policy) => policy?.status === 'active' && daysUntil(policy.end_date) !== null && daysUntil(policy.end_date) < 0;

// Totals for the summary tiles (active policies only)
export const summarisePolicies = (policies) => policies.reduce((acc, policy) => {
  if (policy.status !== 'active') return acc;
  const due = dueInfo(policy);
  const isDue = Boolean(due && due.days <= DUE_SOON_DAYS);
  return {
    count: acc.count + 1,
    cover: acc.cover + (Number(policy.sum_assured) || 0),
    lifeCover: acc.lifeCover + (['term', 'life'].includes(policy.policy_type) ? Number(policy.sum_assured) || 0 : 0),
    healthCover: acc.healthCover + (policy.policy_type === 'health' ? Number(policy.sum_assured) || 0 : 0),
    annualPremium: acc.annualPremium + annualPremium(policy),
    dueCount: acc.dueCount + (isDue ? 1 : 0),
    dueAmount: acc.dueAmount + (isDue ? Number(policy.premium_amount) || 0 : 0),
  };
}, { count: 0, cover: 0, lifeCover: 0, healthCover: 0, annualPremium: 0, dueCount: 0, dueAmount: 0 });

// Compact Indian amount for tiles: ₹1.5 Cr, ₹25 L, ₹45,000
export const formatCover = (amount) => {
  const value = Number(amount) || 0;
  const trim = (n) => n.toFixed(2).replace(/\.?0+$/, '');
  if (value >= 1e7) return `₹${trim(value / 1e7)} Cr`;
  if (value >= 1e5) return `₹${trim(value / 1e5)} L`;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
};

export const formatRupees = (amount, digits = 0) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits })
    .format(Number(amount) || 0);
